/**
 *  @swagger
 *  definitions:
 *    RegattaLogParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        AdminId:
 *          description: ID of the associated Admin
 *          type: string
 *          nullable: true
 *        ModelName:
 *          description: Model Name of the RegattaLog
 *          type: string
 *        ModelId:
 *          description: Model Id of the RegattaLog
 *          type: string
 *        ModelContent:
 *          description: Model Content of the RegattaLog
 *          type: string
 *          nullable: true
 *        Action:
 *          description: Action of the RegattaLog
 *          $ref: '#/components/enums/RegattaLogAction'
 *        Changes:
 *          description: Changes of the RegattaLog
 *          type: object
 *
 *  @swagger
 *  definitions:
 *    RegattaLogCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaLogParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - ModelName
 *            - ModelId
 *            - Action
 *            - Changes
 *
 *  @swagger
 *  definitions:
 *    RegattaLogQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaLog
 *              type: string
 *        - $ref: '#/definitions/RegattaLogParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaLogUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaLogQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaLog:
 *      allOf:
 *        - $ref: '#/definitions/RegattaLogCreateParams'
 *        - $ref: '#/definitions/RegattaLogUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaLog:
 *        allOf:
 *          - $ref: '#/definitions/RegattaLog'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaLog
 *                $ref: '#/components/schemas/Regatta'
 *              Admin:
 *                description: Admin of the RegattaLog
 *                $ref: '#/components/schemas/Admin'
 */

// Enums
import {
  RegattaLogAction,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  AdminId: null | string;
  RegattaId: string;
};

export type $Params = {
  Action: RegattaLogAction;
  Changes: {
    next: Record<string, unknown>;
    prev: null | Record<string, unknown>;
  };
  ModelContent: null | string;
  ModelId: string;
  ModelName: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Admin?: $Model<'Admin'>;
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Action: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaLogAction)),
    validate: {
      notEmpty: true,
    },
  },
  Changes: {
    allowNull: false,
    type: Sequelize.JSON,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  ModelContent: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ModelId: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ModelName: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaLog'> => {
  const RegattaLog = stack.define(
    'RegattaLog',
    fields(Sequelize),
    {
      tableName: 'RegattaLogs',
    },
  );

  RegattaLog.associate = (models: $ModelClasses) => {
    models.RegattaLog.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaLog.belongsTo(
      models.Admin,
      {
        as: 'Admin',
        foreignKey: 'AdminId',
      },
    );
  };

  RegattaLog.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'DESC']],
    },
  );

  return RegattaLog;
});
